// inv/src/components/investor-page/modules/pDealsChart.jsx
import { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import googleLogo from '../../../pages/authentication/google-logo.svg';

function InvestorDealsChart({ data = [] }) {
  const { t, i18n } = useTranslation();
  const [displayedData, setDisplayedData] = useState('allDeals');

  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const dataKeyText = {
    allDeals: t('pageInvProf.graphBtn1'),
    leadInvestor: t('pageInvProf.graphBtn2'),
    secondInvestor: t('pageInvProf.graphBtn3'),
  };

  const getVisibleDataCount = () => {
    if (!data || data.length === 0) return 0;
    const totalPoints = data.length;
    if (totalPoints <= 3) return totalPoints;
    return Math.min(4, Math.max(3, Math.floor(totalPoints * 0.6)));
  };

  const visibleCount = getVisibleDataCount();
  const hasHiddenData = data && data.length > visibleCount;
  const visibleData = data.slice(0, visibleCount);
  const hiddenData = hasHiddenData ? data.slice(visibleCount) : [];
  const additionalYearsCount = hiddenData.length;

  if (!mounted) {
    return <div style={{ height: 270 }} />;
  }

  if (!data || data.length === 0) {
    return <div style={{ height: 270 }} />;
  }

  const generateSignupLink = () => {
    const prefix = i18n.language === 'en' ? '' : `/${i18n.language}`;
    return `${prefix}/signup`;
  };

  return (
    <div>
      <div className="hidden lg:flex">
        <div className="flex-1">
          <ResponsiveContainer width="100%" height={270}>
            <LineChart data={visibleData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <XAxis dataKey="year" />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(value) => `${value} deals`} />
              <Legend />
              {displayedData === 'allDeals' && (
                <Line
                  type="monotone"
                  dataKey="allDeals"
                  stroke="oklch(0.45 0.085 224.283)"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                  name={dataKeyText.allDeals}
                />
              )}
              {displayedData === 'leadInvestor' && (
                <Line
                  type="monotone"
                  dataKey="leadInvestor"
                  stroke="oklch(0.268 0.007 34.298)"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                  name={dataKeyText.leadInvestor}
                />
              )}
              {displayedData === 'secondInvestor' && (
                <Line
                  type="monotone"
                  dataKey="secondInvestor"
                  stroke="oklch(0.453 0.124 130.933)"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                  name={dataKeyText.secondInvestor}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {hasHiddenData && (
          <div className="w-1/3">
            <div className="w-full rounded-lg overflow-hidden">
              <img
                src="https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/CTA_images/investor_profile_graph_data.webp"
                alt="Investor profile chart"
                className="w-[70%] h-auto mx-auto object-contain"
              />
            </div>

            <div className="text-center mt-4">
              <p className="text-sm text-gray-600 mb-2">
                {t('chart.unlockFullChart')}
              </p>
              <p className="text-xs text-blue-600 font-medium mb-4">
                {t('chart.additionalYears', { count: additionalYearsCount })}
              </p>
              <Link to={generateSignupLink()}>
                <Button size="sm" className="flex items-center mx-auto">
                  <Mail className="h-4 w-4 mr-2" />
                  <img src={googleLogo} alt="Google" className="h-4 w-4 mr-1" />
                  {t('pageHome.content.t24')}
                </Button>
              </Link>
            </div>
          </div>
        )}

      </div>

      <div className="lg:hidden">
        <div className="mb-4">
          <ResponsiveContainer width="100%" height={270}>
            <LineChart data={visibleData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <XAxis dataKey="year" />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(value) => `${value} deals`} />
              <Legend />
              {displayedData === 'allDeals' && (
                <Line
                  type="monotone"
                  dataKey="allDeals"
                  stroke="oklch(0.45 0.085 224.283)"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                  name={dataKeyText.allDeals}
                />
              )}
              {displayedData === 'leadInvestor' && (
                <Line
                  type="monotone"
                  dataKey="leadInvestor"
                  stroke="oklch(0.268 0.007 34.298)"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                  name={dataKeyText.leadInvestor}
                />
              )}
              {displayedData === 'secondInvestor' && (
                <Line
                  type="monotone"
                  dataKey="secondInvestor"
                  stroke="oklch(0.453 0.124 130.933)"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                  name={dataKeyText.secondInvestor}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {hasHiddenData && (
          <div className="flex flex-col sm:flex-row items-center bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 mb-4">
            <div className="w-full sm:w-1/2 flex justify-center mb-4 sm:mb-0">
              <img
                src="https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/CTA_images/investor_profile_graph_data.webp"
                alt="Investor profile chart"
                className="max-w-full h-auto object-contain"
              />
            </div>

            <div className="w-full sm:w-1/2 text-center sm:text-left">
              <p className="text-sm text-gray-700 mb-2">
                {t('chart.unlockFullChart')}
              </p>
              <p className="text-sm text-blue-600 font-medium mb-3">
                {t('chart.additionalYears', { count: additionalYearsCount })}
              </p>
              <Link to={generateSignupLink()}>
                <Button
                  size="sm"
                  className="flex items-center px-3 py-1.5 text-sm mx-auto sm:mx-0"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  <img src={googleLogo} alt="Google" className="h-4 w-4 mr-1" />
                  {t('pageHome.content.t24')}
                </Button>
              </Link>
            </div>
          </div>
        )}

      </div>
      <div className="flex p-4 gap-2">
        <Badge
          text={t('pageInvProf.graphBtn1')}
          onClick={() => setDisplayedData('allDeals')}
          className={`cursor-pointer ${displayedData === 'allDeals'
            ? 'bg-cyan-800 text-white'
            : 'bg-cyan-800 text-white hover:bg-cyan-600 hover:text-white'}`}
        />
        <Badge
          text={t('pageInvProf.graphBtn2')}
          onClick={() => setDisplayedData('leadInvestor')}
          className={`cursor-pointer ${displayedData === 'leadInvestor'
            ? 'bg-stone-800 text-white'
            : 'bg-stone-800 text-white hover:bg-stone-600'}`}
        />
        <Badge
          text={t('pageInvProf.graphBtn3')}
          onClick={() => setDisplayedData('secondInvestor')}
          className={`cursor-pointer ${displayedData === 'secondInvestor'
            ? 'bg-lime-800 text-white'
            : 'bg-lime-800 text-white hover:bg-lime-600'}`}
        />
      </div>
    </div>
  );
}

export default InvestorDealsChart;